/**
 * ActionUI
 * 
 * Main action interface for combat, exploration, and story
 * Uses FolkloreContainer components for beautiful UI
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FolkloreContainer } from './FolkloreContainer';
import { MiniIsometricMap } from './MiniIsometricMap';
import { QuestSectionDisplay } from './QuestSectionDisplay';
import { CombatUI } from './CombatUI';
import { Player, Monster, CombatState } from '../types/player';
import { TerrainType, TERRAIN_NAMES, TerrainMap, AxialCoord } from '../types/terrain';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { useAuth } from '../contexts/AuthContext';
import * as questManager from '../utils/questManager';
import { FEATURES } from '../config/features';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { getOutOfStaminaMessage } from '../utils/dailyReset';
import './ActionUI.css';

// Helper function to format time remaining
function formatTime(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  
  if (totalSeconds >= 3600) {
    // HH:MM:SS for times >= 1 hour
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else {
    // MM:SS for times < 1 hour
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}

// Helper function to get terrain display name
function getTerrainName(terrain: TerrainType): string {
  return TERRAIN_NAMES[terrain] || 'Unknown Terrain';
}

export interface ActionUIProps {
  player?: Player;
  monster?: Monster;
  combatState?: CombatState;
  setCombatState?: (state: CombatState | null) => void;
  storyText?: string | null;
  audioUrl?: string | null;
  isLoading?: boolean;
  onAction?: (action: string) => void;
  terrain?: TerrainType | null;
  showStory?: boolean;
  monsterName?: string;
  isInCombat?: boolean;
  showCombatUI?: boolean; // Force combat UI to show (when Fight button clicked)
  combatUI?: React.ReactNode;
  onTerrainImageClick?: () => void;  // Called when terrain image is clicked (not in combat)
  // Story/Monster props
  hasMonster?: boolean;
  onFightClick?: () => void;
  isUnexploredHex?: boolean; // Flag to show "Unexplored Land" indicator
  // Mini isometric map props
  map?: TerrainMap;
  playerPosition?: AxialCoord;
  exploredHexes?: Set<string>;
  onHexClick?: (coord: AxialCoord) => void;
  onViewMapClick?: () => void;
  // Quest status callback
  onQuestStatusChange?: (isQuestActive: boolean) => void;
  onNextQuestHexChange?: (hex: { q: number; r: number } | null) => void;
  // Quest state (from PlayerContext - single source of truth)
  questPlaythrough?: any | null; // questManager.QuestPlaythrough
  questDataLoaded?: boolean;
  updateQuestPlaythrough?: (playthrough: any | null) => void;
  // Player update function
  updatePlayer?: (updates: Partial<Player>) => void;
  updatePosition?: (position: AxialCoord) => void;
  // Travel state props
  travelState?: {
    destinationHex: AxialCoord;
    arrivalTime: number;
    route: AxialCoord[];
    currentRouteIndex: number;
    nextHexArrivalTime: number;
  } | null;
  destinationTerrain?: TerrainType;
  onTravelComplete?: () => void;
  onHexArrival?: (hex: AxialCoord) => void; // Called when arriving at each hex during travel
  plannedRoute?: AxialCoord[];
  // Foraging props
  foragingState?: {
    completionTime: number;
    terrainType: TerrainType;
  } | null;
  onRationClick?: () => void;
  onForagingComplete?: () => void;
  onForageClick?: () => void;
  onRestHealClick?: () => void;
  // Resting props
  restingState?: {
    completionTime: number;
  } | null;
  onRestingComplete?: () => void;
  // Action completion message
  actionCompletionMessage?: string | null;
  onActionComplete?: (message: string) => void;
  pendingDestination?: AxialCoord | null;
  onAnimationComplete?: () => void;
  isStoryReady?: boolean;
}

const ActionUIComponent: React.FC<ActionUIProps> = ({
  player,
  monster,
  combatState,
  setCombatState,
  storyText = null,
  audioUrl = null,
  isLoading = false,
  onAction,
  terrain,
  showStory = true,
  monsterName,
  isInCombat = false,
  showCombatUI = false,
  combatUI,
  onTerrainImageClick,
  hasMonster = false,
  onFightClick,
  isUnexploredHex = false,
  map,
  playerPosition,
  exploredHexes,
  onHexClick,
  onViewMapClick,
  onQuestStatusChange,
  onNextQuestHexChange,
  questPlaythrough: questPlaythroughProp,
  questDataLoaded = false,
  updateQuestPlaythrough,
  updatePlayer,
  updatePosition,
  travelState = null,
  destinationTerrain,
  onTravelComplete,
  onHexArrival,
  foragingState = null,
  onRationClick,
  onForagingComplete,
  onForageClick,
  onRestHealClick,
  restingState = null,
  onRestingComplete,
  actionCompletionMessage = null,
  onActionComplete,
  pendingDestination,
  onAnimationComplete,
  isStoryReady = false,
}) => {
  const { user } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [foragingTimeRemaining, setForagingTimeRemaining] = useState(0);
  const [restingTimeRemaining, setRestingTimeRemaining] = useState(0);
  
  // Quest state - now comes from props (PlayerContext is single source of truth)
  const questPlaythrough = questPlaythroughProp;
  const [isQuestLoading, setIsQuestLoading] = useState(false);
  const [nextQuestHex, setNextQuestHex] = useState<{ q: number; r: number; terrain?: string; direction?: string } | null>(null);
  const [excludedQuestIds, setExcludedQuestIds] = useState<string[]>([]); // Client-side cache of recently abandoned quests
  const [currentHexIsUnexplored, setCurrentHexIsUnexplored] = useState(false); // Track if current hex is unexplored
  
  // Quest combat now uses combatState.isQuestCombat flag instead of local state
  
  // Refs for travel countdown to prevent infinite loops
  const travelHasCompletedRef = useRef(false);
  const onTravelCompleteRef = useRef(onTravelComplete);
  const renderCountRef = useRef(0);
  
  // Use shared audio player (persists across view changes)
  const audioPlayer = useAudioPlayer();
  const { isPlaying, audioError, toggle: toggleAudio } = audioPlayer;
  
  // Track renders
  renderCountRef.current++;
  console.log(`🎨 [ACTION_UI] Render #${renderCountRef.current}`);
  console.log('   storyText:', storyText ? `${storyText.substring(0, 50)}...` : 'null');
  console.log('   audioUrl:', audioUrl);

  // Track component mount/unmount
  useEffect(() => {
    console.log('🎭 [ACTION_UI] Component MOUNTED');
    return () => {
      console.log('🎭 [ACTION_UI] Component UNMOUNTING');
    };
  }, []);
  
  // Keep callback ref in sync (doesn't trigger effect re-run)
  useEffect(() => {
    onTravelCompleteRef.current = onTravelComplete;
  }, [onTravelComplete]);

  // Notify parent when next quest hex changes (for map highlighting)
  useEffect(() => {
    if (onNextQuestHexChange) {
      onNextQuestHexChange(nextQuestHex);
    }
  }, [nextQuestHex, onNextQuestHexChange]);
  
  // DEBUG: Track when actionCompletionMessage is received
  useEffect(() => {
    if (actionCompletionMessage) {
      console.log('📨 ActionUI received actionCompletionMessage:', actionCompletionMessage);
    }
  }, [actionCompletionMessage]);

  // Check for active quest function - UI logic only (PlayerContext handles data loading)
  const checkForActiveQuest = async () => {
    if (!user || !playerPosition) return;
    
    try {
      // PlayerContext already loaded the active quest - just use the prop!
      // This function only handles UI-specific logic (nextQuestHex restoration)
      
      if (questPlaythrough) {
        // RESTORE nextQuestHex - check current playthrough first, then previous quest
        console.log('🔍 [UI LOGIC] Checking for nextQuestHex restoration...');
        console.log('🔍 [UI LOGIC] Current quest index:', questPlaythrough.currentQuestIndex);
        console.log('🔍 [UI LOGIC] Total sections in current playthrough:', questPlaythrough.sections?.length);
        
        let foundNextQuestHex = false;
        
        // Try to find in current playthrough first
        if (questPlaythrough.sections && questPlaythrough.sections.length > 0) {
          const concludedSections = questPlaythrough.sections.filter((s: questManager.QuestSection) => s.concludes);
          if (concludedSections.length > 0) {
            const lastConcludedSection = concludedSections[concludedSections.length - 1];
            if (lastConcludedSection.nextQuestHex) {
              console.log('✅ [UI LOGIC] Found nextQuestHex in current playthrough');
              setNextQuestHex({ q: lastConcludedSection.nextQuestHex.q, r: lastConcludedSection.nextQuestHex.r });
              foundNextQuestHex = true;
            }
          }
        }
        
        // If not found and we're on Quest 2+, check previous quest's playthrough
        if (!foundNextQuestHex && (questPlaythrough.currentQuestIndex || 0) > 0) {
          console.log('🔍 [UI LOGIC] No nextQuestHex in current playthrough, checking previous quest...');
          try {
            const previousQuestHex = await questManager.getPreviousQuestNextHex(
              user.uid,
              questPlaythrough.campaignId || '',
              (questPlaythrough.currentQuestIndex || 1) - 1
            );
            if (previousQuestHex) {
              console.log('✅ [UI LOGIC] Found nextQuestHex from previous quest:', previousQuestHex);
              setNextQuestHex({ q: previousQuestHex.q, r: previousQuestHex.r });
            } else {
              console.log('ℹ️ [UI LOGIC] No nextQuestHex found in previous quest either');
            }
          } catch (error) {
            console.error('Error loading previous quest nextQuestHex:', error);
          }
        }
        
        return; // Quest is active, no need to check for completed quests
      }
      
      // No active quest - check for completed quests with nextQuestHex (restore red hex after reload)
      const nextQuestHexInfo = await questManager.getNextQuestHexForPlayer(user.uid);
      if (nextQuestHexInfo) {
        console.log('🗺️ [UI LOGIC] Restoring red hex from completed campaign:', nextQuestHexInfo);
        setNextQuestHex({ q: nextQuestHexInfo.q, r: nextQuestHexInfo.r });
      }
    } catch (error) {
      console.error('Error in checkForActiveQuest UI logic:', error);
    }
  };

  // Check for active quest when component mounts or user changes
  // Don't re-check on every position change (quests span multiple hexes)
  // Wait for PlayerContext to finish loading before running UI logic
  useEffect(() => {
    if (questDataLoaded) {
      checkForActiveQuest();
    }
  }, [user, questDataLoaded]); // Wait for questDataLoaded before running
  
  // Notify parent when quest status changes (for disabling Map button)
  useEffect(() => {
    // Quest is only "active" if it exists, has sections, and is NOT concluded
    const hasQuestData = questPlaythrough !== null && (questPlaythrough?.sections?.length || 0) > 0;
    const lastSection = questPlaythrough?.sections?.[questPlaythrough.sections.length - 1];
    const isQuestConcluded = lastSection?.concludes === true;
    
    // If quest is concluded, allow player to move (isQuestActive = false)
    const isQuestActive = hasQuestData && !isQuestConcluded;
    
    onQuestStatusChange?.(isQuestActive);
  }, [questPlaythrough, onQuestStatusChange]);
  
  // Clear completed quest when player moves to a new hex
  // Don't run until PlayerContext has loaded quest data
  useEffect(() => {
    if (!questDataLoaded || !questPlaythrough || !playerPosition) return;
    
    const lastSection = questPlaythrough.sections?.[questPlaythrough.sections.length - 1];
    const isQuestConcluded = lastSection?.concludes === true;
    const questStartHex = questPlaythrough.startHex;
    
    // If quest is concluded and player has moved from the starting hex, clear it
    if (isQuestConcluded && questStartHex) {
      const hasMoved = playerPosition.q !== questStartHex.q || playerPosition.r !== questStartHex.r;
      if (hasMoved) {
        console.log('🧹 Clearing completed quest - player has moved from starting hex');
        updateQuestPlaythrough?.(null);
        setNextQuestHex(null);
      }
    }
  }, [playerPosition, questPlaythrough]);

  // Handle travel countdown and hex-by-hex progression
  useEffect(() => {
    if (!travelState) {
      setTimeRemaining(0);
      travelHasCompletedRef.current = false; // Reset when travel state clears
      return;
    }

    // Track if we've already processed a hex arrival in this effect cycle
    let hasProcessedHexArrival = false;
    
    // Update countdown every 100ms
    const interval = setInterval(() => {
      // CRITICAL: Check if travelState is null (travel was stopped, e.g., by monster encounter)
      // This prevents any further encounter checks after travel stops
      if (!travelState) {
        console.log('🛑 TRAVEL DEBUG - Interval detected travelState is null, stopping all checks');
        clearInterval(interval);
        setTimeRemaining(0);
        return;
      }
      
      // Check if we've arrived at the next hex in the route
      const nextHexTimeRemaining = travelState.nextHexArrivalTime - Date.now();
      
      // Only process ONE hex arrival per effect cycle to avoid rapid-fire checks
      if (nextHexTimeRemaining <= 0 && !hasProcessedHexArrival) {
        // Double-check travelState is still valid before proceeding
        if (!travelState) {
          console.log('🛑 TRAVEL DEBUG - Travel stopped during hex arrival check, aborting');
          clearInterval(interval);
          return;
        }
        
        // Mark as processed - this flag will be reset when useEffect re-runs with new travelState
        hasProcessedHexArrival = true;
        
        // Arrived at next hex - check for monster encounter
        const currentHexIndex = travelState.currentRouteIndex + 1;
        if (currentHexIndex < travelState.route.length) {
          const arrivedHex = travelState.route[currentHexIndex];
          // Call onHexArrival to check for monster and handle progression
          // When advanceTravelToHex is called in App.tsx, it will update travelState,
          // triggering this useEffect to re-run with the new nextHexArrivalTime
          if (onHexArrival) {
            onHexArrival(arrivedHex);
          }
        }
      }
      
      // Check if we've reached the final destination
      // Only complete travel when we've actually progressed through all hexes
      const currentIndex = travelState.currentRouteIndex;
      const totalHexes = travelState.route.length;
      const hasReachedFinalDestination = currentIndex >= totalHexes - 1;
      
      // Check final destination arrival time (for display and completion)
      const finalRemaining = travelState.arrivalTime - Date.now();
      
      // Only complete travel if:
      // 1. We've reached the final hex in the route (currentIndex >= totalHexes - 1)
      // 2. AND the final arrival time has passed
      if (hasReachedFinalDestination && finalRemaining <= 0) {
        clearInterval(interval);
        setTimeRemaining(0);
        
        // Only fire callback once using REF (persists across re-renders!)
        if (onTravelCompleteRef.current && !travelHasCompletedRef.current) {
          travelHasCompletedRef.current = true;
          onTravelCompleteRef.current();
        }
      } else {
        // Show time remaining until next hex arrival (for multi-hex travel)
        setTimeRemaining(Math.max(0, nextHexTimeRemaining));
      }
    }, 100);

    return () => clearInterval(interval);
  }, [travelState?.arrivalTime, travelState?.nextHexArrivalTime, travelState?.currentRouteIndex, travelState?.route, travelState, onHexArrival, destinationTerrain]); // Re-run when travel state changes

  // Handle foraging countdown
  useEffect(() => {
    if (!foragingState) {
      setForagingTimeRemaining(0);
      return;
    }

    // Update countdown every 100ms
    const interval = setInterval(() => {
      const remaining = foragingState.completionTime - Date.now();
      
      if (remaining <= 0) {
        clearInterval(interval);
        setForagingTimeRemaining(0);
        if (onForagingComplete) {
          onForagingComplete();
        }
      } else {
        setForagingTimeRemaining(remaining);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [foragingState, onForagingComplete]);

  // Handle resting countdown
  useEffect(() => {
    if (!restingState) {
      setRestingTimeRemaining(0);
      return;
    }

    // Update countdown every 100ms
    const interval = setInterval(() => {
      const remaining = restingState.completionTime - Date.now();
      
      if (remaining <= 0) {
        clearInterval(interval);
        setRestingTimeRemaining(0);
        if (onRestingComplete) {
          onRestingComplete();
        }
      } else {
        setRestingTimeRemaining(remaining);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [restingState, onRestingComplete]);

  // Quest handler - directly starts quest when clicked
  const handleQuestClick = async () => {
    if (!user || !playerPosition) return;
    
    // If quest is already active, do nothing (user is already viewing it)
    if (questPlaythrough) {
      console.log('📜 Quest already active, ignoring click');
      return;
    }
    
    // Start quest directly
    await handleBeginQuest();
  };

  const handleBeginQuest = async () => {
    console.log('🎬 handleBeginQuest called', {
      user: user?.uid,
      playerPosition,
      terrain,
      terrainName: terrain !== null && terrain !== undefined ? TERRAIN_NAMES[terrain] : 'undefined'
    });
    
    // Clear any action completion message when starting a quest
    if (onActionComplete) {
      onActionComplete('');
    }
    
    if (!user || !playerPosition || !map || terrain === null || terrain === undefined) {
      console.error('❌ Cannot begin quest - missing required data:', {
        hasUser: !!user,
        hasPosition: !!playerPosition,
        hasMap: !!map,
        hasTerrain: terrain !== null && terrain !== undefined,
        terrainValue: terrain
      });
      return;
    }
    
    setIsQuestLoading(true);
    
    try {
      // Get current hex to read faction data
      const { hexKey } = await import('../utils/hexMath');
      const currentHex = map.hexes.get(hexKey(playerPosition));
      const faction = currentHex?.faction || null;
      
      console.log('📍 Current hex faction:', faction, 'at', playerPosition);
      
      const terrainName = TERRAIN_NAMES[terrain];
      
      // Generate or load quest (terrain-based, not hex-based)
      // Pass userId to exclude quests player has completed/abandoned
      const { quest, section1, wasGenerated, cacheHit, isUnexploredHex } = await questManager.getOrGenerateQuest(
        terrainName,
        playerPosition,
        faction,
        [player?.name || 'Player'],
        player?.maxStamina || 12,
        'brynlore',
        user.uid,
        excludedQuestIds // Pass client-side exclusion list
      );
      
      // Track if this hex is unexplored for UI indicator
      setCurrentHexIsUnexplored(isUnexploredHex || false);
      
      console.log(`✅ Quest ${wasGenerated ? 'generated' : 'loaded'} for ${terrainName}`, {
        questId: quest.id,
        cacheHit,
        type: quest.type,
        isUnexploredHex: isUnexploredHex || false
      });
      
      // Start quest playthrough
      const playthrough = await questManager.startQuestPlaythrough(
        user.uid,
        quest.id,
        terrainName,
        playerPosition
      );
      
      console.log('✅ Quest playthrough started:', {
        questId: quest.id,
        playthroughId: playthrough.id,
        terrainType: terrainName
      });
      
      // Add section1 to playthrough for UI compatibility
      const playthroughWithSection = {
        ...playthrough,
        sections: [section1],
        currentQuestIndex: 0,
        campaignId: quest.id
      };
      
      updateQuestPlaythrough?.(playthroughWithSection);
      setIsQuestLoading(false);
      
    } catch (error: any) {
      console.error('Error beginning quest:', error);
      
      const errorMessage = error?.message || 'Unknown error occurred';
      const errorDetails = error?.code || '';
      
      alert(
        `Failed to begin quest:\n\n${errorMessage}\n\n` +
        `${errorDetails ? `Error code: ${errorDetails}\n\n` : ''}` +
        `Please check the browser console (F12) for more details, or contact support.`
      );
      
      setIsQuestLoading(false);
    }
  };

  const handleAbandonCampaign = async () => {
    if (!questPlaythrough || !playerPosition) return;
    
    try {
      // Stop audio narration when abandoning quest
      audioPlayer.pause();
      
      const result = await questManager.abandonCampaign(questPlaythrough.id, playerPosition);
      
      // Add to client-side exclusion list immediately (don't wait for Firestore trigger)
      if (result && result.questId) {
        console.log(`📝 Adding quest ${result.questId} to client-side exclusion list`);
        setExcludedQuestIds(prev => [...prev, result.questId]);
      }
      
      updateQuestPlaythrough?.(null);
    } catch (error) {
      console.error('Error abandoning campaign:', error);
    }
  };

  // NEW: Quest combat handlers
  const handleQuestCombatInitiate = (monsterName: string, monsterStats: { hp: number; attackDie: number; damageDie: number }) => {
    console.log(`🗡️ Initiating quest combat with ${monsterName}`, monsterStats);
    
    if (!player || !setCombatState) {
      console.error('Cannot initiate quest combat: missing player or setCombatState');
      return;
    }
    
    // Use the unified combatState system with quest flag
    const questCombatState: CombatState = {
      isActive: true,
      player: player,
      party: [player],
      partySize: 1,
      monster: {
        id: `quest-${monsterName}`,
        name: monsterName,
        currentHp: monsterStats.hp,
        maxHp: monsterStats.hp,
        attack: 0, // Not used in new system
        attackDie: monsterStats.attackDie,
        damageDie: monsterStats.damageDie,
        defense: 0,
        imagePath: `/assets/monsters/${monsterName.toLowerCase()}.png`,
      },
      storyLines: [],
      round: 0,
      canFlee: false, // Quest combat doesn't allow fleeing
      isQuestCombat: true,
      questId: questPlaythrough?.questId || null,
    };
    
    setCombatState(questCombatState);
    console.log('✅ Quest combat state initialized in PlayerContext');
  };
  
  const handleQuestCombatFlee = async () => {
    console.log('🏃 Player fled from quest combat - CAMPAIGN FAILED');
    
    const monsterName = combatState?.monster?.name || 'monster';
    
    // Fleeing from quest combat FAILS the campaign (harsh penalty)
    if (questPlaythrough && playerPosition) {
      try {
        const result = await questManager.abandonCampaign(questPlaythrough.id, playerPosition);
        
        // Add to client-side exclusion list immediately
        if (result && result.questId) {
          console.log(`📝 Adding quest ${result.questId} to client-side exclusion list (fled combat)`);
          setExcludedQuestIds(prev => [...prev, result.questId]);
        }
        
        updateQuestPlaythrough?.(null);
        setCombatState?.(null); // Clear quest combat state
        
        // Show fleeing message
        if (onActionComplete) {
          onActionComplete(`You fled from the ${monsterName}. The quest has failed.`);
        }
      } catch (error) {
        console.error('Error handling flee:', error);
      }
    }
  };
  
  const handleQuestCombatEnd = async (result: 'victory' | 'defeat' | 'fled') => {
    console.log(`⚔️ Quest combat ended. Result: ${result}`);
    console.log('   player:', !!player);
    console.log('   user:', !!user);
    console.log('   questPlaythrough:', !!questPlaythrough);
    console.log('   map:', !!map);
    
    if (result === 'victory') {
      // Player won - update HP and generate next section
      if (player && user && questPlaythrough && map) {
        console.log('✅ All required data present, proceeding with victory handler...');
        try {
          // Player HP should already be updated by usePlayer hook during combat
          // Just need to persist to Firestore
          const playerRef = doc(db, 'players', user.uid);
          await updateDoc(playerRef, {
            hp: player.hp
          });
          
          console.log(`✅ Player survived combat with ${player.hp} HP remaining`);
          
          // Clear quest combat state
          setCombatState?.(null);
          
          // Auto-generate next section after a brief delay (no intermediate message)
          setTimeout(async () => {
            console.log('🔄 Auto-generating next section after combat victory...');
            console.log('   questPlaythrough:', questPlaythrough);
            console.log('   sections:', questPlaythrough.sections);
            console.log('   sections length:', questPlaythrough.sections?.length);
            
            const currentSection = questPlaythrough.sections?.[questPlaythrough.sections.length - 1];
            console.log('   currentSection:', currentSection);
            
            if (!currentSection) {
              console.error('❌ No current section found! Cannot auto-generate next section.');
              return;
            }
            
            // Combat sections have no choices by design - generate next section with null choiceId
            const continueChoice = currentSection.choices[0];
            console.log('   continueChoice:', continueChoice);
            
            if (continueChoice) {
              console.log(`✅ Found continue choice with ID: ${continueChoice.id}`);
              console.log('   Calling handleMakeQuestChoice...');
              try {
                await handleMakeQuestChoice(continueChoice.id);
                console.log('✅ handleMakeQuestChoice completed successfully');
              } catch (error) {
                console.error('❌ Error in handleMakeQuestChoice:', error);
              }
            } else {
              console.log('✅ Combat section (no choices) - generating next section with null choiceId');
              try {
                await handleMakeQuestChoice(null as any);
                console.log('✅ handleMakeQuestChoice completed successfully (post-combat)');
              } catch (error) {
                console.error('❌ Error in handleMakeQuestChoice (post-combat):', error);
              }
            }
          }, 1500);
        } catch (error) {
          console.error('Error updating player HP after combat:', error);
        }
      } else {
        console.error('❌ Missing required data for victory handler!');
        console.error('   Missing:', {
          player: !player,
          user: !user,
          questPlaythrough: !questPlaythrough,
          map: !map
        });
      }
    } else if (result === 'defeat') {
      // Player died - RESPAWN at spawn hex
      console.log('💀 Player died in quest combat - RESPAWNING');
      
      if (questPlaythrough && playerPosition && player) {
        try {
          const monsterName = combatState?.monster?.name || 'monster';
          await questManager.abandonCampaign(questPlaythrough.id, playerPosition);
          updateQuestPlaythrough?.(null);
          setCombatState?.(null); // Clear quest combat state
          
          // RESPAWN: Reset HP and teleport to spawn hex
          if (user) {
            // Check both new and old field names for backwards compatibility
            const spawnHex = player.spawnHex || (player as any).homeHex || { q: 100, r: 100 }; // Fallback to default
            const playerRef = doc(db, 'players', user.uid);
            
            await updateDoc(playerRef, {
              hp: player.maxHp, // Reset to full HP
              position: spawnHex, // Teleport to spawn
            });
            
            if (updatePlayer) {
              updatePlayer({ hp: player.maxHp });
            }
            
            // Update position in app
            if (updatePosition) {
              updatePosition(spawnHex);
            }
            
            console.log(`⚰️ Respawned at spawn hex: (${spawnHex.q}, ${spawnHex.r})`);
          }
          
          // Show defeat and respawn message
          if (onActionComplete) {
            onActionComplete(
              `You were defeated by the ${monsterName}.\n\n` +
              `You awaken at your settlement, wounds healed but the quest lost.`
            );
          }
        } catch (error) {
          console.error('Error handling defeat:', error);
        }
      }
    } else if (result === 'fled') {
      // Fleeing is already handled by handleQuestCombatFlee, but handle here too for completeness
      await handleQuestCombatFlee();
    }
  };

  const handleMakeQuestChoice = async (choiceId: string | null) => {
    if (!questPlaythrough || !map) return;
    
    // CRITICAL: Check stamina BEFORE generating next section (to avoid wasting API calls)
    // Quests consume 1 stamina per section (no movement)
    if (player && player.stamina < 1) {
      console.warn('⚠️ Quest progression blocked - no stamina');
      const message = getOutOfStaminaMessage();
      if (onActionComplete) {
        onActionComplete(message);
      }
      return; // Block quest progression
    }
    
    setIsQuestLoading(true);
    
    try {
      // Quests don't move - always use current player position
      if (!playerPosition) {
        throw new Error('Player position not available');
      }
      
      const hexKey = `${playerPosition.q},${playerPosition.r}`;
      const currentHexData = map.hexes.get(hexKey);
      if (!currentHexData || currentHexData.terrain === undefined) {
        throw new Error(`Current hex not found or has no terrain: ${hexKey}`);
      }
      const currentHexWithTerrain = { ...playerPosition, terrain: TERRAIN_NAMES[currentHexData.terrain] };
      
      // Get adjacent hexes for context (still used by backend)
      const adjacentHexes = getAdjacentHexesForQuest(playerPosition, map);
      
      // Track the choice that was made (for NPC/choice history)
      let choiceText = null;
      if (choiceId && questPlaythrough.sections && questPlaythrough.sections.length > 0) {
        const currentSection = questPlaythrough.sections[questPlaythrough.sections.length - 1];
        const selectedChoice = currentSection.choices?.find((c: questManager.QuestChoice) => c.id === choiceId);
        if (selectedChoice) {
          choiceText = selectedChoice.text;
        }
      }
      
      // Generate next section (choiceId can be null for combat sections)
      const nextSection = await questManager.makeQuestChoice(
        questPlaythrough.id,
        choiceId!,
        currentHexWithTerrain,
        adjacentHexes,
        questPlaythrough.visitedHexes
      );
      
      // Update playthrough with new section AND choice history
      if (questPlaythrough) {
        // Build updated choice history
        const updatedChoiceHistory = [...(questPlaythrough.choiceHistory || [])];
        if (choiceId && choiceText) {
          updatedChoiceHistory.push({
            section: questPlaythrough.currentSection,
            choiceId: choiceId,
            choiceText: choiceText
          });
          
          // Persist choice history to Firestore
          const playthroughRef = doc(db, 'questPlaythroughs', questPlaythrough.id);
          updateDoc(playthroughRef, {
            choiceHistory: updatedChoiceHistory
          }).catch(error => {
            console.error('Error updating choice history:', error);
          });
        }
        
        const updatedPlaythrough = {
          ...questPlaythrough,
          sections: [...(questPlaythrough.sections || []), nextSection],
          choiceHistory: updatedChoiceHistory
        };
        
        updateQuestPlaythrough?.(updatedPlaythrough);
      }
      
      // Consume stamina (1 per section, no movement)
      if (player && user) {
        const newStamina = player.stamina - 1;
          
        // Update Firestore (stamina only - no position change)
        const playerRef = doc(db, 'players', user.uid);
        await updateDoc(playerRef, {
          stamina: newStamina,
        });
        
        // Update local state immediately for UI responsiveness
        if (updatePlayer) {
          updatePlayer({ stamina: newStamina });
        }
        // No position update - quests don't move the player
      }
      
      // Handle quest completion - just mark playthrough as complete
      // Sponsorship will be handled by QuestSectionDisplay UI
      if (nextSection.concludes) {
        console.log('🎉 Quest completed!');
        
        try {
          if (user) {
            await questManager.completeQuestPlaythrough(
              questPlaythrough.id,
              questPlaythrough.questId,
              user.uid
              // Don't pass startHex - no auto-binding
            );
            console.log('✅ Quest playthrough marked as complete');
            console.log('⏳ Awaiting sponsorship decision from player');
          }
        } catch (completionError) {
          console.error('Error completing quest:', completionError);
        }
        
        // DON'T clear quest state here - let section 12 render!
        // QuestSectionDisplay will show the conclusion UI with sponsorship choice
        // Quest state will clear when player closes quest after sponsorship decision
      }
      
      // TODO: Move player to target hex if different from current position
      
    } catch (error) {
      console.error('Error making quest choice:', error);
      // Show error message to user
      if (onActionComplete) {
        onActionComplete(
          'Quest generation failed. This section encountered an error.\n\n' +
          'You can try abandoning this quest and starting a new one.'
        );
      }
    } finally {
      setIsQuestLoading(false);
    }
  };

  // Helper to get adjacent hexes with terrain info
  const getAdjacentHexesForQuest = (hex: AxialCoord, terrainMap: TerrainMap) => {
    const directions = ['NE', 'E', 'SE', 'SW', 'W', 'NW'] as const;
    const neighborOffsets = [
      { q: 1, r: -1 }, // NE
      { q: 1, r: 0 },  // E
      { q: 0, r: 1 },  // SE
      { q: -1, r: 1 }, // SW
      { q: -1, r: 0 }, // W
      { q: 0, r: -1 }, // NW
    ];
    
    return neighborOffsets.map((offset, index) => {
      const neighborQ = hex.q + offset.q;
      const neighborR = hex.r + offset.r;
      const neighborHex = terrainMap.hexes.get(`${neighborQ},${neighborR}`);
      
      return {
        q: neighborQ,
        r: neighborR,
        terrain: neighborHex?.terrain !== undefined ? TERRAIN_NAMES[neighborHex.terrain] : 'Unknown',
        direction: directions[index],
      };
    });
  };

  // Audio is now managed by shared AudioPlayerContext - no local audio handling needed
  
  // Debug logging
  const isCombat = combatState?.isActive || false;
  // Use monsterName to create monster object if available, otherwise use passed monster or combatState monster
  // Prioritize combatState.monster during combat as it has updated HP, but only if it matches monsterName
  const currentMonster = React.useMemo(() => {
    // During combat, use combatState.monster which has updated HP, but only if it matches the monsterName
    // Also check if combatState is actually active (isInCombat) to avoid using stale data
    if (combatState?.monster && isInCombat) {
      // Check if the combatState monster matches the monsterName we want to display
      const monsterNameLower = monsterName?.toLowerCase();
      const combatMonsterNameLower = combatState.monster.name.toLowerCase();
      if (monsterNameLower && combatMonsterNameLower === monsterNameLower) {
        return combatState.monster;
      }
    }
    
    // Quest combat now uses combatState.monster (no special handling needed)
    
    // Prioritize monsterName over monster prop - if we have a monsterName, use it to create the monster
    // This ensures we show the correct monster (e.g., Troll) even if monster prop has old data (e.g., Fairy)
    if (monsterName) {
      const { initializeMonster } = require('../utils/combat');
      const monsterData = initializeMonster(monsterName, 1);
      if (monsterData) {
        return {
          id: `monster-${monsterName}`,
          name: monsterData.name,
          currentHp: monsterData.currentHp,
          maxHp: monsterData.maxHp,
          attack: monsterData.attack,
          imagePath: `/assets/monsters/${monsterName.toLowerCase()}.png`,
        };
      }
    }
    // Fallback to monster prop if no monsterName
    if (monster) {
      return monster;
    }
    return null;
  }, [monsterName, monster, combatState?.monster, combatState?.monster?.currentHp, isInCombat]);
  const currentPlayer = player || combatState?.player;

  // Format dice display for d10 (1-10)
  const formatDice = (rolls?: number[]): string[] => {
    if (!rolls || rolls.length === 0) return [];
    return rolls.map(roll => {
      const diceFaces = ['⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];
      if (roll >= 1 && roll <= 6) {
        return diceFaces[roll - 1];
      } else if (roll >= 7 && roll <= 10) {
        return `[${roll}]`;
      }
      return '⚀';
    });
  };

  const playerDice = formatDice(combatState?.playerRolls);
  const playerTotal = combatState?.partyTotal || combatState?.playerRolls?.reduce((a, b) => a + b, 0) || 0;
  // Monster doesn't roll - it has a fixed attack score
  const monsterAttack = combatState?.monsterAttack || currentMonster?.attack || 0;

  // Get image path - monster if in combat, otherwise terrain background image
  const getImagePath = (): string => {
    if (isInCombat && (monsterName || currentMonster)) {
      // In combat - show monster
      const name = (monsterName || currentMonster?.name || 'fairy').toLowerCase();
      return `/assets/monsters/${name}.png`;
    } else if (terrain !== null && terrain !== undefined) {
      // Not in combat - show terrain background image (same as screen background)
      const terrainName = TERRAIN_NAMES[terrain].toLowerCase();
      return `/assets/backgrounds/${terrainName}-background.png`;
    }
    // Fallback
    return '/assets/backgrounds/forest-background.png';
  };
  const displayImagePath = getImagePath();

  // Get terrain-specific background
  const getTerrainBackground = (terrainType: TerrainType | null | undefined): string => {
    // Check if terrain is null or undefined (but allow 0 which is Mountain)
    if (terrainType === null || terrainType === undefined) {
      // Try to get terrain from map if available
      if (map && playerPosition) {
        const hex = map.hexes.get(`${playerPosition.q},${playerPosition.r}`);
        if (hex?.terrain !== null && hex?.terrain !== undefined) {
          const terrainName = TERRAIN_NAMES[hex.terrain].toLowerCase();
          return `/assets/backgrounds/${terrainName}-background.png`;
        }
      }
      // Default fallback if no terrain selected
      return '/assets/backgrounds/parchment_Dan.png';
    }
    
    const terrainName = TERRAIN_NAMES[terrainType].toLowerCase();
    return `/assets/backgrounds/${terrainName}-background.png`;
  };

  // Get effective terrain (from prop or map)
  const effectiveTerrain = React.useMemo(() => {
    if (terrain !== null && terrain !== undefined) {
      return terrain;
    }
    // Fallback to map if terrain prop is null
    if (map && playerPosition) {
      const hex = map.hexes.get(`${playerPosition.q},${playerPosition.r}`);
      return hex?.terrain ?? null;
    }
    return null;
  }, [terrain, map, playerPosition]);

  // Initialize background immediately
  const initialBackground = getTerrainBackground(effectiveTerrain);
  
  // Track previous terrain for fade effect
  const previousTerrainRef = useRef<TerrainType | null | undefined>(effectiveTerrain);
  const [backgroundOpacity, setBackgroundOpacity] = useState(1);
  const [currentBackground, setCurrentBackground] = useState<string>(initialBackground);
  const [previousBackground, setPreviousBackground] = useState<string>(initialBackground);

  // Handle terrain change with fade effect
  useEffect(() => {
    const newBackground = getTerrainBackground(effectiveTerrain);
    
    // Always ensure background is set (handles initial load and reload)
    if (!currentBackground || 
        currentBackground === '/assets/backgrounds/parchment_Dan.png' ||
        (effectiveTerrain !== null && effectiveTerrain !== undefined && previousTerrainRef.current === null)) {
      // Initial load, reload, or terrain just became available - set without fade
      setCurrentBackground(newBackground);
      setPreviousBackground(newBackground);
      previousTerrainRef.current = effectiveTerrain;
      setBackgroundOpacity(1);
      return;
    }
    
    // If terrain changed, fade to new background
    if (previousTerrainRef.current !== effectiveTerrain) {
      // Set previous background to current before changing
      setPreviousBackground(currentBackground);
      
      // Start fade transition
      setBackgroundOpacity(0);
      
      // After fade starts, update current background
      setTimeout(() => {
        setCurrentBackground(newBackground);
        setBackgroundOpacity(1);
      }, 100); // Delay for smoother transition start
      
      previousTerrainRef.current = effectiveTerrain;
    } else if (currentBackground !== newBackground) {
      // Same terrain but background path changed (shouldn't happen, but safety check)
      setCurrentBackground(newBackground);
      setPreviousBackground(newBackground);
    }
  }, [effectiveTerrain, map, playerPosition, currentBackground]);

  // Use current background, fallback to initial if not set yet
  const activeBackground = currentBackground || initialBackground;
  const fadeBackground = previousBackground || initialBackground;

  // Base container style (no background - handled by layers)
  const containerStyle: React.CSSProperties = {
    minWidth: '100vw',
    minHeight: '100vh',
    position: 'relative',
    backgroundColor: 'transparent', // Ensure container doesn't block background layers
  };

  // Background layer styles with fade
  const baseBackgroundStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundAttachment: 'fixed',
    zIndex: -2, // Match existing background z-index
  };

  const previousLayerStyle: React.CSSProperties = {
    ...baseBackgroundStyle,
    backgroundImage: fadeBackground ? `url(${fadeBackground})` : 'none',
    backgroundColor: '#2a2a2a', // Darker fallback color (visible)
    opacity: 1 - backgroundOpacity,
    transform: `scale(${1 + (backgroundOpacity * 0.03)})`, // Subtle zoom out as it fades
    filter: `brightness(${0.95 - (backgroundOpacity * 0.15)})`, // Dim as it fades
    transition: 'all 1.5s cubic-bezier(0.4, 0.0, 0.2, 1)', // Material Design smooth easing
    pointerEvents: 'none',
  };

  const currentLayerStyle: React.CSSProperties = {
    ...baseBackgroundStyle,
    backgroundImage: activeBackground ? `url(${activeBackground})` : 'none',
    backgroundColor: '#2a2a2a', // Darker fallback color (visible)
    opacity: backgroundOpacity,
    transform: `scale(${1 + ((1 - backgroundOpacity) * 0.03)})`, // Subtle zoom in as it fades
    filter: `brightness(${0.95 - ((1 - backgroundOpacity) * 0.15)})`, // Brighten as it fades
    transition: 'all 1.5s cubic-bezier(0.4, 0.0, 0.2, 1)', // Material Design smooth easing
    pointerEvents: 'none',
  };

  return (
    <div className="action-ui" style={containerStyle}>
      {/* Layered backgrounds for fade effect */}
      <div style={previousLayerStyle} />
      <div style={currentLayerStyle} />
      <div className="action-ui__overlay" />
      
      {/* LEFT COLUMN - Visual + Stats */}
      <div className="action-ui__left-column">
        {/* 1. Map or Monster Picture */}
        <div className="action-ui__visual-container">
          {!isInCombat && map && playerPosition && exploredHexes && onHexClick && onViewMapClick ? (
            <MiniIsometricMap
              map={map!}
              center={playerPosition!}
              playerPosition={playerPosition!}
              exploredHexes={exploredHexes!}
              onHexClick={onHexClick!}
              onViewMapClick={onViewMapClick!}
              player={player}
              playerStamina={player?.stamina || 0}
              currentTerrain={terrain !== null && terrain !== undefined ? terrain : undefined}
              onForageClick={onForageClick}
              canForage={
                !travelState && 
                !foragingState && 
                !restingState &&
                (player?.rations ?? 0) < (player?.maxRations ?? 10) && 
                terrain !== null && 
                terrain !== undefined &&
                terrain !== 0 /* Desert */ && 
                terrain !== 4 /* Settlement */ &&
                terrain !== 7 /* Ocean */
              }
              canMove={!travelState && !foragingState && !restingState && !(hasMonster && !isInCombat)}
              isInCombat={isInCombat}
              onRestHealClick={onRestHealClick}
              pendingDestination={pendingDestination}
              onAnimationComplete={onAnimationComplete}
              isStoryReady={isStoryReady}
            />
          ) : (
            <img 
              src={displayImagePath} 
              alt={isInCombat ? (currentMonster?.name || monsterName || 'Creature') : (terrain !== null && terrain !== undefined ? TERRAIN_NAMES[terrain] : 'Terrain')}
              className="creature-image"
              style={{ 
                cursor: !isInCombat && onTerrainImageClick ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (!isInCombat && onTerrainImageClick) {
                  onTerrainImageClick();
                }
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                if (target.parentElement) {
                  target.parentElement.innerHTML = `<div class="creature-fallback">🐉</div>`;
                }
              }}
            />
          )}
        </div>

        {/* 2. Stamina OR Monster Stats (conditional - same space) */}
        {(isInCombat || combatState?.isQuestCombat) && currentMonster ? (
          <FolkloreContainer variant="stat" title={currentMonster.name}>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <img 
                  src="/assets/ui/hit-points.svg" 
                  alt="Hit Points" 
                  style={{ width: '20px', height: '20px' }}
                />
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#2c1810' }}>
                  {currentMonster.currentHp}
                </span>
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#8b5a2b' }}>
                  {' '}/ {currentMonster.maxHp}
                </span>
              </div>
              {'attackDie' in currentMonster && currentMonster.attackDie && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <img 
                      src="/assets/ui/attack-score.svg" 
                      alt="Attack" 
                      style={{ width: '20px', height: '20px' }}
                    />
                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#8b5a2b' }}>
                      {currentMonster.attackDie}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <img 
                      src="/assets/ui/damage-score.svg" 
                      alt="Damage" 
                      style={{ width: '20px', height: '20px' }}
                    />
                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#8b5a2b' }}>
                      {'damageDie' in currentMonster ? currentMonster.damageDie : '?'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <img
                      src="/assets/ui/shield-defense.svg"
                      alt="Defense"
                      style={{ width: '20px', height: '20px' }}
                    />
                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#8b5a2b' }}>
                      {'defense' in currentMonster ? currentMonster.defense : 0}
                    </span>
                  </div>
                </>
              )}
            </div>
          </FolkloreContainer>
        ) : (
          <FolkloreContainer variant="stat" title="Stamina">
            <div className="stat-value">
              {currentPlayer?.stamina ?? 16}
            </div>
            <div className="stat-max">
              / {currentPlayer?.maxStamina ?? 12}
            </div>
          </FolkloreContainer>
        )}

        {/* 3. Combat Stats (always present) */}
        <FolkloreContainer variant="stat" title="Player Combat Stats">
          <div style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <img 
                src="/assets/ui/hit-points.svg" 
                alt="Hit Points" 
                style={{ width: '20px', height: '20px' }}
              />
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#2c1810' }}>
                {currentPlayer?.hp ?? 60}
              </span>
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#8b5a2b' }}>
                {' '}/ {currentPlayer?.maxHp ?? 60}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <img 
                src="/assets/ui/attack-score.svg" 
                alt="Attack" 
                style={{ width: '20px', height: '20px' }}
              />
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#8b5a2b' }}>11</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <img 
                src="/assets/ui/damage-score.svg" 
                alt="Damage" 
                style={{ width: '20px', height: '20px' }}
              />
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#8b5a2b' }}>10</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <img
                src="/assets/ui/shield-defense.svg"
                alt="Defense"
                style={{ width: '20px', height: '20px' }}
              />
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#8b5a2b' }}>2</span>
            </div>
          </div>
        </FolkloreContainer>
      </div>

      {/* RIGHT COLUMN - Story/Quest/Combat Content */}
      <div className="action-ui__right-column">
        {combatState?.isQuestCombat && combatState.monster && player ? (
          // PRIORITY 1: Quest combat (unified combatState system with isQuestCombat flag)
          <CombatUI
            monsterName={combatState.monster.name}
            party={[player]}
            partySize={1}
            onCombatEnd={handleQuestCombatEnd}
            onFlee={handleQuestCombatFlee}
          />
        ) : (isInCombat || showCombatUI) && combatUI ? (
          // PRIORITY 2: Exploration combat
          combatUI
        ) : showStory && !isInCombat && !showCombatUI ? (
          <FolkloreContainer variant="story">
            <div className="story-content">
              {FEATURES.MANUAL_STAMINA_RECOVERY && restingState && restingTimeRemaining > 0 ? (
                // Show resting countdown
                <div style={{ textAlign: 'center' }}>
                  <p className="story-loading" style={{ fontSize: '18px', marginBottom: '12px' }}>
                    Resting and recovering...
                  </p>
                  <p style={{ 
                    fontSize: '48px', 
                    fontWeight: 'bold', 
                    color: '#4169E1',
                    fontFamily: "'Cinzel', 'Lora', 'Georgia', serif",
                    margin: '16px 0',
                    letterSpacing: '0.05em',
                  }}>
                    {formatTime(restingTimeRemaining)}
                  </p>
                  <p style={{ fontSize: '14px', color: '#6B5A4A', opacity: 0.8 }}>
                    You rest your weary body and regain health
                  </p>
                </div>
              ) : FEATURES.MANUAL_STAMINA_RECOVERY && foragingState && foragingTimeRemaining > 0 ? (
                // Show foraging countdown
                <div style={{ textAlign: 'center' }}>
                  <p className="story-loading" style={{ fontSize: '18px', marginBottom: '12px' }}>
                    Foraging in {foragingState.terrainType !== undefined ? getTerrainName(foragingState.terrainType) : 'the wilderness'}...
                  </p>
                  <p style={{ 
                    fontSize: '48px', 
                    fontWeight: 'bold', 
                    color: '#228B22',
                    fontFamily: "'Cinzel', 'Lora', 'Georgia', serif",
                    margin: '16px 0',
                    letterSpacing: '0.05em',
                  }}>
                    {formatTime(foragingTimeRemaining)}
                  </p>
                  <p style={{ fontSize: '14px', color: '#6B5A4A', opacity: 0.8 }}>
                    You gather rations from the land
                  </p>
                </div>
              ) : FEATURES.TRAVEL_TIMERS && travelState && timeRemaining > 0 ? (
                // Show travel countdown
                <div style={{ textAlign: 'center' }}>
                  <p className="story-loading" style={{ fontSize: '18px', marginBottom: '12px' }}>
                    Travelling to {destinationTerrain ? getTerrainName(destinationTerrain) : 'destination'}...
                  </p>
                  <p style={{ 
                    fontSize: '48px', 
                    fontWeight: 'bold', 
                    color: '#8B4513',
                    fontFamily: "'Cinzel', 'Lora', 'Georgia', serif",
                    margin: '16px 0',
                    letterSpacing: '0.05em',
                  }}>
                    {formatTime(timeRemaining)}
                  </p>
                  <p style={{ fontSize: '14px', color: '#6B5A4A', opacity: 0.8 }}>
                    Your story will begin when you arrive
                  </p>
                </div>
              ) : isLoading ? (
                // Show loading state
                <div style={{ textAlign: 'center' }}>
                  <p className="story-loading" style={{ fontSize: '18px', marginBottom: '12px' }}>
                    Travelling...
                  </p>
                </div>
              ) : isQuestLoading ? (
                // Show quest generation loading state
                <div style={{ 
                  textAlign: 'center',
                  padding: '40px 20px',
                  minHeight: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div className="quest-loading-spinner" />
                  <p style={{ marginTop: '20px', fontSize: '18px', fontFamily: "'Cinzel', serif" }}>
                    QUESTING...
                  </p>
                </div>
              ) : questPlaythrough && (questPlaythrough.sections?.length || 0) > 0 && !combatState?.isQuestCombat ? (
                // Show quest section with choices (not in combat)
                // Pass actionCompletionMessage to display alongside quest content
                <QuestSectionDisplay
                  questPlaythrough={questPlaythrough}
                  currentSection={questPlaythrough.sections?.[questPlaythrough.sections.length - 1]!}
                  onMakeChoice={handleMakeQuestChoice}
                  onAbandonCampaign={handleAbandonCampaign}
                  onInitiateCombat={handleQuestCombatInitiate}
                  onFleeCombat={handleQuestCombatFlee}
                  isLoading={isQuestLoading}
                  outOfStaminaMessage={actionCompletionMessage}
                />
              ) : actionCompletionMessage ? (
                // ⚠️ PARALLEL CONTENT: Changes here may need to be made in StoryContainer.tsx (~line 223)
                // Show action completion message (only if not in quest)
                <div style={{ textAlign: 'center' }}>
                  <p className="story-line" style={{ 
                    fontSize: '18px',
                    margin: '24px 0',
                    lineHeight: '1.6',
                  }}>
                    {actionCompletionMessage}
                  </p>
                </div>
              ) : storyText ? (
                // Show story text
                <div style={{ position: 'relative' }}>
                  <p className="story-line">
                    {storyText}
                  </p>
                  
                  {/* Show Unexplored Land indicator UNDER the story text */}
                  {(currentHexIsUnexplored || isUnexploredHex) && (
                    <div style={{
                      backgroundColor: 'transparent',
                      border: '1px solid #C4B5A0',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      marginTop: '12px',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#5C4A3A',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}>
                      You are in unexplored land
                    </div>
                  )}
                  
                  {hasMonster && onFightClick && (
                    <button 
                      className="story-fight-button"
                      onClick={onFightClick}
                    >
                      You are forced to fight...
                    </button>
                  )}
                  
                  {/* Quest button - show when no monster and no active quest */}
                  {!hasMonster && !questPlaythrough && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      marginTop: '1.5rem' 
                    }}>
                      <button
                        onClick={handleQuestClick}
                        style={{
                          padding: '0',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title="Start a Quest"
                      >
                        <img
                          src="/assets/ui/quest-button.svg"
                          alt="Quest"
                          style={{
                            width: '120px',
                            height: 'auto',
                            display: 'block',
                          }}
                        />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                // Default placeholder
                <div>
                  <p className="story-line">
                    You step into the unknown. The landscape stretches before you, filled with quiet mysteries.
                  </p>
                  
                  {/* Quest button - show when no monster and no active quest */}
                  {!hasMonster && !questPlaythrough && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      marginTop: '1.5rem' 
                    }}>
                      <button
                        onClick={handleQuestClick}
                        style={{
                          padding: '0',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title="Start a Quest"
                      >
                        <img
                          src="/assets/ui/quest-button.svg"
                          alt="Quest"
                          style={{
                            width: '120px',
                            height: 'auto',
                            display: 'block',
                          }}
                        />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </FolkloreContainer>
        ) : null}
      </div>
    </div>
  );
};

// Export memoized version to prevent unnecessary re-renders
export const ActionUI = React.memo(ActionUIComponent);
